import { ReactNode } from "react";
import { Undo2, X } from "lucide-react";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import { Combobox } from "@/components/Base/Combobox";
import { Switch } from "@/components/Base/Switch";
import { Button } from "@/components/Base/Button";
import { Tooltip } from "@/components/Common/Tooltip";
import { cn } from "@/lib/utils";

export interface ContainerLinkGroupSync {
  checked: boolean;
  disabled: boolean;
  onCheckedChange?: (checked: boolean) => void;
  tooltip?: string;
  /** Some links sync and some don't: render "Mixed n/total" + All on / All off in place of the Switch. */
  mixed?: {
    onCount: number;
    total: number;
    onAllOn: () => void;
    onAllOff: () => void;
  };
  /** The user changed this row: shows a CHANGED pill next to the label and (uniform rows) a Revert link. */
  changed?: boolean;
  onRevert?: () => void;
}

export interface ContainerLinkGroupRow {
  key: string;
  avatar: ReactNode;
  label: string;
  /** Secondary line under the label (e.g. bulk coverage: "2 of 6 containers"). */
  subtitle?: string;
  /** Small pill next to the label (e.g. "NEW"). */
  badge?: string;
  sync?: ContainerLinkGroupSync;
  /** Omit for rows that must not be removable. */
  onRemove?: () => void;
  removeTooltip?: string;
  /**
   * The link is pending removal (WEBV3-346): struck-through label, a REMOVING
   * pill, and an Undo icon button in the remove slot (which then ignores
   * `onRemove`). An icon, not text: the last grid column is a fixed 32px.
   */
  removing?: { onUndo: () => void; undoTooltip?: string };
}

export interface ContainerLinkGroupProps {
  icon: IconNames;
  title: string;
  showSyncColumn: boolean;
  rows: ContainerLinkGroupRow[];
  emptyText: string;
  addOptions: { value: string; label: string }[];
  addPlaceholder: string;
  /** Placeholder of the search box inside the add picker, e.g. "Search technicians…". */
  addSearchPlaceholder: string;
  /** Shown when the search matches nothing (or nothing is left to add). */
  addEmptyText: string;
  /** Called once with the picked option's value. */
  onAddSelect: (value: string) => void;
}

/**
 * One "Linked technicians" / "Linked source labels" section of the redesigned
 * container Edit dialog (WEBV3-282 follow-up): a header, one row per linked
 * item with an optional two-way-sync Switch and a remove button, an empty
 * state, and a searchable single-select Combobox to link more.
 */
export const ContainerLinkGroup = ({
  icon,
  title,
  showSyncColumn,
  rows,
  emptyText,
  addOptions,
  addPlaceholder,
  addSearchPlaceholder,
  addEmptyText,
  onAddSelect,
}: ContainerLinkGroupProps) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 border-b pb-1.5">
        <div className="flex h-[22px] w-[22px] items-center justify-center rounded-md bg-accent/10 text-primary">
          <Icon name={icon} size={14} />
        </div>
        <div className="text-sm font-semibold text-black">{title}</div>
        <div className="flex-1" />
        {showSyncColumn && (
          <div className="pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Two-way sync
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-input p-3 text-center text-sm text-muted-foreground">
          {emptyText}
        </div>
      ) : (
        rows.map((row) => (
          <div
            key={row.key}
            className="grid grid-cols-[26px_minmax(0,1fr)_auto_32px] items-center gap-3 py-1"
          >
            {row.avatar}
            <div className="flex min-w-0 flex-col">
              <div className="flex items-center gap-1.5 text-sm text-black">
                <span
                  className={cn(
                    "truncate",
                    row.removing && "text-muted-foreground line-through"
                  )}
                >
                  {row.label}
                </span>
                {row.removing && (
                  <span className="shrink-0 rounded-full bg-destructive px-2 py-px text-[11px] font-semibold text-destructive-foreground">
                    REMOVING
                  </span>
                )}
                {row.badge && (
                  <span className="shrink-0 rounded-full bg-accent px-2 py-px text-[11px] font-semibold text-accent-foreground">
                    {row.badge}
                  </span>
                )}
                {row.sync?.changed && (
                  <span className="shrink-0 rounded-full bg-yellow-300 px-2 py-px text-[11px] font-semibold text-black">
                    CHANGED
                  </span>
                )}
              </div>
              {row.subtitle && (
                <div className="truncate text-xs text-muted-foreground">
                  {row.subtitle}
                </div>
              )}
            </div>
            {row.sync ? (
              row.sync.mixed ? (
                <div className="flex items-center gap-1.5">
                  <span className="whitespace-nowrap text-xs font-semibold text-muted-foreground">
                    {`Mixed ${row.sync.mixed.onCount}/${row.sync.mixed.total}`}
                  </span>
                  <Tooltip message={row.sync.tooltip}>
                    <span className="inline-flex">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={row.sync.disabled}
                        aria-label={`All on for ${row.label}`}
                        onClick={row.sync.mixed.onAllOn}
                      >
                        All on
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip message={row.sync.tooltip}>
                    <span className="inline-flex">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={row.sync.disabled}
                        aria-label={`All off for ${row.label}`}
                        onClick={row.sync.mixed.onAllOff}
                      >
                        All off
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              ) : (
                <div className="flex items-center gap-2.5">
                  {row.sync.changed && row.sync.onRevert && (
                    <button
                      type="button"
                      className="rounded-sm text-xs text-primary underline ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      aria-label={`Revert two-way sync for ${row.label}`}
                      onClick={row.sync.onRevert}
                    >
                      Revert
                    </button>
                  )}
                  <Tooltip message={row.sync.tooltip}>
                    {/* Tooltip's asChild slots its own open/closed data-state
                        directly onto whatever it wraps. Slotted straight onto
                        the Switch, that clobbers the Switch's own
                        data-state="checked"/"unchecked" -- exactly what
                        data-[state=checked]:bg-accent keys off -- leaving the
                        track with no background at all. A plain wrapper absorbs
                        Tooltip's clone instead, the same way Tooltip already
                        protects a *disabled* child by wrapping it in a div. */}
                    <span className="inline-flex">
                      <Switch
                        checked={row.sync.checked}
                        disabled={row.sync.disabled}
                        onCheckedChange={row.sync.onCheckedChange}
                        aria-label={`Two-way sync for ${row.label}`}
                      />
                    </span>
                  </Tooltip>
                </div>
              )
            ) : (
              <div />
            )}
            {row.removing ? (
              <Tooltip message={row.removing.undoTooltip}>
                <Button
                  size="smIcon"
                  type="button"
                  variant="outline"
                  aria-label={row.removing.undoTooltip ?? "Undo"}
                  onClick={row.removing.onUndo}
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
              </Tooltip>
            ) : row.onRemove ? (
              <Tooltip message={row.removeTooltip}>
                <Button
                  size="smIcon"
                  type="button"
                  aria-label={row.removeTooltip}
                  onClick={row.onRemove}
                >
                  <X className="h-4 w-4" />
                </Button>
              </Tooltip>
            ) : (
              <div />
            )}
          </div>
        ))
      )}

      <Combobox
        options={addOptions}
        // Always empty: this control is a trigger, not a persistent
        // selection, so after a pick it snaps back to the placeholder and can
        // add again. Same width precedent as TaskForm's RO picker.
        value=""
        // cmdk trims item values before reporting a pick, so a label stored
        // with padding ("Needs Parts ") would come back trimmed and link under
        // a string the catalog doesn't hold. Hand back the option we supplied.
        onValueChange={(picked) =>
          onAddSelect(
            (
              addOptions.find((option) => option.value === picked) ??
              addOptions.find((option) => option.value.trim() === picked)
            )?.value ?? picked
          )
        }
        placeholder={addPlaceholder}
        // role="combobox" isn't named by its text, and a dialog holds two of
        // these (technicians, labels): name each by what it already displays.
        aria-label={addPlaceholder}
        searchPlaceholder={addSearchPlaceholder}
        emptyText={addEmptyText}
        className="w-full font-normal shadow-sm"
        popoverClassName="w-[var(--radix-popover-trigger-width)]"
      />
    </div>
  );
};
